import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { useUploadAvatar } from "@/hooks/useUploadAvatar";
import { Upload, X } from "lucide-react";
import { toast } from "sonner";
import type { Member } from "@/types/Member";

interface UploadAvatarDialogProps {
  member: Member;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUploadSuccess?: () => void;
}

const UploadAvatarDialog = ({ member, open, onOpenChange, onUploadSuccess }: UploadAvatarDialogProps) => {
  const uploadAvatarMutation = useUploadAvatar();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const handleRemoveFile = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setSelectedFile(null);
    setPreviewUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Clear state when dialog closes
  useEffect(() => {
    if (!open) {
      handleRemoveFile();
    }
  }, [open]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (!file) {
      return;
    }

    const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp"];

    if (!allowedTypes.includes(file.type)) {
      toast.error("Invalid file type. Please upload a JPEG, PNG, GIF, or WebP image.");
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    const maxSize = 5 * 1024 * 1024;

    if (file.size > maxSize) {
      toast.error("File is too large. Maximum size is 5MB.");
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error("Please select an image to upload");
      return;
    }

    try {
      await uploadAvatarMutation.mutateAsync({
        memberId: member.id,
        avatarFile: selectedFile,
      });

      toast.success("Avatar uploaded successfully");

      // Trigger success callback to refresh parent view
      if (onUploadSuccess) {
        onUploadSuccess();
      }

      handleClose();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to upload avatar");
    }
  };

  const handleClose = () => {
    handleRemoveFile();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Upload Avatar</DialogTitle>
          <DialogDescription>
            Upload a new avatar image for {member.firstName} {member.lastName}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* File Input */}
          <div className="space-y-2">
            <Label htmlFor="avatar-file">Select Image</Label>
            <input
              ref={fileInputRef}
              id="avatar-file"
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
              onChange={handleFileSelect}
              className="hidden"
            />
            <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()} className="w-full">
              <Upload className="h-4 w-4 mr-2 truncate" />
              {selectedFile ? "Change Image" : "Select Image"}
            </Button>
            <p className="text-xs text-muted-foreground">Supported formats: JPEG, PNG, GIF, WebP. Maximum size: 5MB</p>
          </div>

          {/* Preview */}
          {selectedFile && previewUrl && (
            <div className="space-y-2">
              <Label>Preview</Label>
              <div className="relative border rounded-lg p-4 bg-gray-50">
                <button
                  type="button"
                  onClick={handleRemoveFile}
                  className="absolute top-2 right-2 p-1 bg-white rounded-full shadow-md hover:bg-gray-100"
                >
                  <X className="h-4 w-4" />
                </button>
                <div className="flex flex-col items-center gap-3">
                  <img
                    src={previewUrl}
                    alt="Avatar preview"
                    className="w-40 h-40 rounded-full object-cover border-4 border-white shadow-lg"
                  />
                  <div className="text-center max-w-xs">
                    <p className="text-sm font-medium truncate">{selectedFile.name}</p>
                    <p className="text-xs text-muted-foreground">{(selectedFile.size / 1024 / 1024).toFixed(2)} MB</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={handleClose} disabled={uploadAvatarMutation.isPending}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleUpload}
            disabled={!selectedFile || uploadAvatarMutation.isPending}
          >
            {uploadAvatarMutation.isPending ? "Uploading..." : "Upload Avatar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UploadAvatarDialog;
